import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AuthDialog } from "@/components/AuthDialog";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Star, Building2, TrendingUp, Loader2, ArrowRight, Sparkles } from "lucide-react";

const FREE_FEATURES = ["Standard listing on map & search", "Name, type, location & phone", "“Contact for availability”"];
const PREMIUM_FEATURES = ["Extended profile: photos, amenities & description", "Live open-bed count shown to families", "Inquiries emailed straight to your facility", "Featured — appears first in results"];

export default function ForFacilitiesPage() {
  const { user, refresh } = useAuth();
  const { settings } = useSettings();
  const [authOpen, setAuthOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [loadingFid, setLoadingFid] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [conciergeFee, setConciergeFee] = useState(199);
  const [matrix, setMatrix] = useState(null);
  const [annual, setAnnual] = useState(null);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api.get("/plans").then((r) => { setConciergeFee(r.data.concierge_fee); setMatrix(r.data.matrix); setAnnual({ enabled: r.data.annual_enabled, months: r.data.annual_months }); }).catch(() => {});
  }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("signup") && !user) setAuthOpen(true);
  }, [user]);

  const loadFacilities = useCallback(async () => {
    if (user?.role === "director" || user?.role === "admin") {
      try { const { data } = await api.get("/my/facilities"); setFacilities(data); } catch (e) { console.error("Failed to load facilities", e); }
    }
  }, [user]);

  useEffect(() => { loadFacilities(); }, [loadFacilities]);

  // Post-payment polling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful — your listing has been upgraded!");
          setVerifying(false);
          await refresh();
          loadFacilities();
          window.history.replaceState({}, "", "/for-facilities");
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          toast.error("Payment not completed.");
          setVerifying(false);
          window.history.replaceState({}, "", "/for-facilities");
          return;
        }
      } catch (e) { console.error("Payment status poll failed", e); }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const upgrade = async (facilityId) => {
    setLoadingFid(facilityId);
    try {
      const { data } = await api.post("/payments/checkout", {
        facility_id: facilityId, billing, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingFid(null);
    }
  };

  const annualMonths = annual?.months || 10;
  const priceFor = (f) => {
    const m = f.list_price ?? 0;
    return billing === "annual" ? m * annualMonths : m;
  };

  const isDirector = user?.role === "director" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />

      {verifying && (
        <div className="fixed inset-0 z-[60] bg-white/70 backdrop-blur flex items-center justify-center">
          <div className="flex items-center gap-3 text-[#1A365D]"><Loader2 className="h-6 w-6 animate-spin" /> Verifying payment...</div>
        </div>
      )}

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E3F3ED] text-[#1A365D] text-sm font-medium mb-6"><Building2 className="h-4 w-4" /> For facilities</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1C211F] leading-[1.05]">{settings?.for_fac_title || "Reach families the moment they're searching."}</h1>
          <p className="text-lg text-[#4A534E] mt-6 max-w-xl leading-relaxed">{settings?.for_fac_subtitle || "Claim your free listing, then upgrade to put your photos, amenities, and live bed availability in front of families and hospital discharge planners across Colorado."}</p>
          <div className="flex gap-3 mt-8">
            <Link to="/claim"><Button className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-12 px-6" data-testid="ff-claim-btn">Claim your facility <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-2xl">
          <img src={settings?.for_fac_image || "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100"} alt="Healthcare professional" className="w-full h-[420px] object-cover" />
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <h2 className="font-heading text-3xl font-medium text-[#1C211F] mb-2">{settings?.ff_plans_title || "Listing plans"}</h2>
        <p className="text-[#4A534E] mb-10">{settings?.ff_plans_subtitle}</p>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
          <div data-testid="plan-free" className="rounded-2xl p-7 border border-[#E8E6DF] bg-white flex flex-col">
            <h3 className="font-heading text-2xl font-medium text-[#1C211F]">{settings?.plan_free_name || "Free"}</h3>
            <p className="mb-5 text-[#1C211F]"><span className="font-mono-data text-3xl font-semibold">$0</span><span className="text-sm ml-1 text-[#5C6660]">forever</span></p>
            <ul className="space-y-3 flex-1">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4.5 w-4.5 shrink-0 mt-0.5 text-[#0077B6]" /><span className="text-[#4A534E]">{f}</span></li>
              ))}
            </ul>
          </div>

          <div data-testid="plan-premium" className="rounded-2xl p-7 border border-[#1A365D] bg-[#1A365D] text-white shadow-xl flex flex-col">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="font-heading text-2xl font-medium text-white">{settings?.plan_premium_name || "Premium"}</h3>
              <span className="text-[11px] font-semibold uppercase tracking-wide bg-[#0077B6] px-2 py-1 rounded-full">Best value</span>
            </div>
            <p className="mb-1 text-white/90">
              <span className="font-mono-data text-3xl font-semibold">from ${matrix ? matrix.alr_small : 19}</span>
              <span className="text-sm ml-1 text-white/60">per month</span>
            </p>
            <p className="text-xs text-white/60 mb-5">Your exact price depends on facility type & licensed beds (see below).{annual?.enabled ? ` Pay annually and get ${12 - annualMonths} months free.` : ""}</p>
            <ul className="space-y-3 flex-1">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4.5 w-4.5 shrink-0 mt-0.5 text-[#7FC4E8]" /><span className="text-white/90">{f}</span></li>
              ))}
            </ul>
            {!isDirector && (
              <Button onClick={() => setAuthOpen(true)} className="w-full mt-6 rounded-xl bg-white text-[#1A365D] hover:bg-[#E3F3ED]" data-testid="get-premium">Create a director account</Button>
            )}
          </div>
        </div>

        {matrix && (
          <div className="mt-10 max-w-4xl" data-testid="pricing-matrix">
            <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">{settings?.ff_matrix_title || "Premium pricing by facility type & size"}</h3>
            <div className="overflow-x-auto rounded-2xl border border-[#E8E6DF] bg-white">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-[#5C6660] border-b border-[#E8E6DF] bg-[#F7F6F2]">
                  <th scope="col" className="py-3 px-5">Facility type</th><th scope="col" className="py-3 px-5">Licensed beds</th><th scope="col" className="py-3 px-5 text-right">Price / month</th>
                </tr></thead>
                <tbody className="divide-y divide-[#F2F1EC]">
                  <tr><th scope="rowgroup" rowSpan={3} className="py-3 px-5 font-normal text-left align-top">Assisted Living <span className="text-[#5C6660]">(ALR / ALR·ACF / ALR·BISL)</span></th><td className="py-3 px-5">Up to 15 beds</td><td className="py-3 px-5 text-right font-mono-data font-semibold">${matrix.alr_small}</td></tr>
                  <tr><td className="py-3 px-5">16–74 beds</td><td className="py-3 px-5 text-right font-mono-data font-semibold">${matrix.alr_mid}</td></tr>
                  <tr><td className="py-3 px-5">75+ beds</td><td className="py-3 px-5 text-right font-mono-data font-semibold">${matrix.alr_large}</td></tr>
                  <tr><td className="py-3 px-5">Nursing <span className="text-[#5C6660]">(NF / SNF / dual-cert)</span></td><td className="py-3 px-5">Any size</td><td className="py-3 px-5 text-right font-mono-data font-semibold">${matrix.nursing}</td></tr>
                  <tr><td className="py-3 px-5">Private Nursing Facility <span className="text-[#5C6660]">(PRIVATE)</span></td><td className="py-3 px-5">Any size</td><td className="py-3 px-5 text-right font-mono-data font-semibold">${matrix.private}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {isDirector && facilities.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-4 md:px-8 py-12">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <h2 className="font-heading text-2xl font-medium text-[#1C211F] flex items-center gap-2"><TrendingUp className="h-6 w-6 text-[#0077B6]" /> Upgrade your facilities</h2>
            {annual?.enabled && (
              <div className="flex items-center gap-1 bg-[#EFEEE8] rounded-xl p-1" data-testid="billing-toggle">
                <button onClick={() => setBilling("monthly")} data-testid="billing-monthly" className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === "monthly" ? "bg-white text-[#1A365D] shadow-sm" : "text-[#5C6660]"}`}>Monthly</button>
                <button onClick={() => setBilling("annual")} data-testid="billing-annual" className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${billing === "annual" ? "bg-white text-[#1A365D] shadow-sm" : "text-[#5C6660]"}`}>
                  Annual <span className="text-[10px] font-semibold uppercase bg-[#E3F3ED] text-[#1A6E4E] px-1.5 py-0.5 rounded-full">{12 - annualMonths} mo free</span>
                </button>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {facilities.map((f) => (
              <div key={f.facility_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="my-facility-row">
                <div>
                  <h3 className="font-heading text-lg font-medium text-[#1C211F]">{f.name}</h3>
                  <p className="text-xs uppercase tracking-wide text-[#5C6660] mt-1">{f.facility_type_detail || f.facility_type} · {f.licensed_beds_total} beds · Current: <span className="text-[#0077B6] font-semibold">{f.tier === "premium" ? "Premium" : "Free"}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  {f.tier === "premium" ? (
                    <span className="inline-flex items-center gap-1 text-sm text-[#0077B6] font-medium px-3"><Star className="h-4 w-4 fill-[#0077B6]" /> Premium active</span>
                  ) : (
                    <Button onClick={() => upgrade(f.facility_id)} disabled={loadingFid === f.facility_id} className="rounded-xl bg-[#0077B6] hover:bg-[#005f93] text-white" data-testid="upgrade-premium-btn">
                      {loadingFid === f.facility_id ? <Loader2 className="h-4 w-4 animate-spin" /> : `Go Premium — $${priceFor(f)}/${billing === "annual" ? "yr" : "mo"}`}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 py-12">
        <div className="rounded-3xl bg-[#1A365D] text-white p-8 md:p-12 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium mb-4"><Sparkles className="h-4 w-4" /> Done-for-you</span>
            <h2 className="font-heading text-3xl font-medium leading-tight">Short on time? We'll build your listing for you.</h2>
            <p className="text-white/80 mt-3 max-w-xl">Our team writes your description, adds your amenities, uploads photos, and gets your profile ready to attract families — a one-time concierge setup.</p>
            <ul className="grid sm:grid-cols-2 gap-2 mt-5 text-sm">
              {["Professional profile copywriting", "Photo curation & upload", "Amenities & care details", "Ready in a few business days"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-white/90"><Check className="h-4 w-4 text-[#7FC4E8]" /> {f}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl p-6 text-[#1C211F] text-center">
            <p className="text-sm text-[#5C6660]">One-time setup</p>
            <p className="font-mono-data text-4xl font-semibold text-[#1A365D] my-1">${conciergeFee}</p>
            <Button onClick={() => (isDirector ? setConciergeOpen(true) : setAuthOpen(true))} data-testid="concierge-btn"
              className="w-full mt-4 bg-[#0077B6] hover:bg-[#005f93] text-white rounded-xl h-12">
              {isDirector ? "Get it done for me" : "Sign up to order"}
            </Button>
            <p className="text-xs text-[#5C6660] mt-3">Secure checkout via Stripe</p>
          </div>
        </div>
      </section>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} defaultRole="director" />
      <ConciergeDialog open={conciergeOpen} onOpenChange={setConciergeOpen} user={user} facilities={facilities} fee={conciergeFee} />
    </div>
  );
}

function ConciergeDialog({ open, onOpenChange, user, facilities, fee }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ facility_id: "", facility_name: "", contact_name: user?.name || "", email: user?.email || "", phone: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/services/concierge/checkout", { ...form, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="concierge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Done-for-you setup — ${fee}</DialogTitle>
          <DialogDescription>Tell us about the facility and we'll prepare a standout listing for you.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {facilities.length > 0 ? (
            <div className="space-y-1.5">
              <Label htmlFor="concierge-facility">Which facility?</Label>
              <select id="concierge-facility" data-testid="concierge-facility" className="w-full h-11 rounded-xl border border-[#E8E6DF] px-3 bg-white"
                value={form.facility_id} onChange={(e) => setForm({ ...form, facility_id: e.target.value })}>
                <option value="">— Select a facility you claimed —</option>
                {facilities.map((f) => <option key={f.facility_id} value={f.facility_id}>{f.name}</option>)}
              </select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="concierge-facility-name">Facility name</Label><Input id="concierge-facility-name" data-testid="concierge-facility-name" value={form.facility_name} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} placeholder="Sunrise Senior Living" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="concierge-name">Your name</Label><Input id="concierge-name" required data-testid="concierge-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label htmlFor="concierge-phone">Phone</Label><Input id="concierge-phone" data-testid="concierge-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="concierge-email">Email</Label><Input id="concierge-email" required type="email" data-testid="concierge-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label htmlFor="concierge-notes">What should we know?</Label><Textarea id="concierge-notes" rows={3} data-testid="concierge-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Care types, photos you can provide, special amenities..." /></div>
          <Button type="submit" disabled={loading} data-testid="concierge-pay" className="w-full bg-[#0077B6] hover:bg-[#005f93] text-white rounded-xl h-11">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay $${fee} & order setup`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
